import type {
  AdminAiFeaturesCounts,
  AdminAiFeaturesLevel,
  AdminAiFeaturesModule,
  AdminAiFeaturesNode,
} from "../models/admin-ai-features.models";
import type {
  AdminAiScanCount,
  AdminAiScanJson,
  AdminAiScanObject,
  AdminAiScanReportFile,
} from "../models/admin-ai-scans.models";

function featureRecord(value: AdminAiScanJson | undefined): AdminAiScanObject {
  return value !== null && typeof value === "object" && !Array.isArray(value) ? value : {};
}

function featureText(value: AdminAiScanJson | undefined, fallback = ""): string {
  return typeof value === "string" && value.trim() ? value : fallback;
}

function emptyFeatureCounts(): AdminAiFeaturesCounts {
  return { businessObjects: 0, surfaces: 0, featureGroups: 0, behaviors: 0 };
}

// === FEATURE ===

function countAiFeaturesChildren(children: AdminAiFeaturesNode[]): AdminAiFeaturesCounts {
  return children.reduce((counts, child) => {
    counts.businessObjects += child.counts.businessObjects + Number(child.level === "businessObject");
    counts.surfaces += child.counts.surfaces + Number(child.level === "surface");
    counts.featureGroups += child.counts.featureGroups + Number(child.level === "featureGroup");
    counts.behaviors += child.counts.behaviors + Number(child.level === "behavior");

    return counts;
  }, emptyFeatureCounts());
}

function prepareAiFeaturesNodes(
  value: AdminAiScanJson | undefined,
  level: AdminAiFeaturesLevel,
): AdminAiFeaturesNode[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap((entry, index) => {
    if (entry === null || typeof entry !== "object" || Array.isArray(entry)) {
      return [];
    }

    const id = featureText(entry.id, `${level}-${index + 1}`);
    let children: AdminAiFeaturesNode[] = [];

    if (level === "businessObject") {
      children = prepareAiFeaturesNodes(entry.surfaces, "surface");
    } else if (level === "surface") {
      children = prepareAiFeaturesNodes(entry.featureGroups, "featureGroup");
    } else if (level === "featureGroup") {
      children = prepareAiFeaturesNodes(entry.behaviors, "behavior");
    }

    return [
      {
        id,
        label: featureText(entry.label, id),
        kind: featureText(entry.kind),
        level,
        children,
        counts: countAiFeaturesChildren(children),
      },
    ];
  });
}

// Project only presentation fields; the module card retains the complete source JSON for inspection.
export function prepareAiFeaturesModules(reports: AdminAiScanReportFile[]): AdminAiFeaturesModule[] {
  return reports
    .map((file) => {
      const metadata = featureRecord(file.report.report);
      const moduleId = featureText(metadata.moduleId, featureText(metadata.id, file.path));
      const children = prepareAiFeaturesNodes(file.report.tree, "businessObject");

      return {
        file,
        metadata,
        moduleId,
        moduleName: featureText(metadata.moduleName, moduleId),
        title: featureText(metadata.title, moduleId),
        children,
        counts: countAiFeaturesChildren(children),
      };
    })
    .sort(
      (left, right) => left.moduleId.localeCompare(right.moduleId) || left.file.path.localeCompare(right.file.path),
    );
}

function filterAiFeaturesNodes(nodes: AdminAiFeaturesNode[], query: string): AdminAiFeaturesNode[] {
  return nodes.flatMap((node) => {
    const matchesParent = `${node.id} ${node.label} ${node.kind}`.toLowerCase().includes(query);

    if (matchesParent) {
      return [node];
    }

    const children = filterAiFeaturesNodes(node.children, query);

    if (!children.length) {
      return [];
    }

    return [{ ...node, children, counts: countAiFeaturesChildren(children) }];
  });
}

// Keep matching ancestors and every child of a matching parent, rather than flattening the report into search rows.
export function filterAiFeaturesModules(
  modules: AdminAiFeaturesModule[],
  moduleId: string,
  search: string,
): AdminAiFeaturesModule[] {
  const query = search.trim().toLowerCase();

  return modules
    .filter((module) => !moduleId || module.moduleId === moduleId)
    .flatMap((module) => {
      if (!query || `${module.moduleId} ${module.moduleName} ${module.title}`.toLowerCase().includes(query)) {
        return [module];
      }

      const children = filterAiFeaturesNodes(module.children, query);

      return children.length ? [{ ...module, children, counts: countAiFeaturesChildren(children) }] : [];
    });
}

export function totalAiFeaturesCounts(modules: AdminAiFeaturesModule[]): AdminAiFeaturesCounts {
  return modules.reduce(
    (counts, module) => ({
      businessObjects: counts.businessObjects + module.counts.businessObjects,
      surfaces: counts.surfaces + module.counts.surfaces,
      featureGroups: counts.featureGroups + module.counts.featureGroups,
      behaviors: counts.behaviors + module.counts.behaviors,
    }),
    emptyFeatureCounts(),
  );
}

// Give descendant totals their own aligned columns instead of mixing numbers into the row title.
export function getAiFeaturesCountItems(counts: AdminAiFeaturesCounts): AdminAiScanCount[] {
  return [
    { label: "BO", value: counts.businessObjects },
    { label: "Surfaces", value: counts.surfaces },
    { label: "Groups", value: counts.featureGroups },
    { label: "Behaviors", value: counts.behaviors },
  ].filter((count) => count.value > 0);
}
